import React from 'react';
import { MessageSquare, Users, FileText, HeartHandshake } from 'lucide-react';
import { howItHelps } from '../../data/mock';

const iconMap = {
  MessageSquare: MessageSquare,
  Users: Users,
  FileText: FileText,
  HeartHandshake: HeartHandshake
};

const HowItHelpsSection = () => {
  return (
    <section className="py-16 sm:py-24 bg-white relative">
      {/* Decorative top wave */}
      <div className="absolute top-0 left-0 right-0 h-8 sm:h-16 bg-gray-50" style={{clipPath: 'ellipse(70% 100% at 50% 0%)'}}></div>
      
      <div className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 pt-4 sm:pt-8">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
            <span className="text-gray-900">How </span>
            <span className="text-teal-500">Doktor Gigi</span>
            <span className="text-gray-900"> Helps</span>
          </h2>
          <p className="text-sm sm:text-lg text-gray-500">
            We don't just connect patients — we guide them
          </p>
        </div>

        {/* Desktop Steps Grid (unchanged) */}
        <div className="hidden sm:grid sm:grid-cols-4 gap-8 relative">
          {howItHelps.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            return (
              <div key={item.id} className="text-center relative z-10">
                {/* Desktop Connector Line */}
                {index < howItHelps.length - 1 && (
                  <div className="absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-teal-300 to-teal-200 rounded-full" />
                )}
                
                {/* Icon Container */}
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-teal-400 to-teal-500 rounded-3xl flex items-center justify-center shadow-lg shadow-teal-500/30 rotate-3 hover:rotate-0 transition-transform duration-300">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  {/* Step Number */}
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md">
                    {item.step}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-xs mx-auto">
                  {item.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Mobile 2x2 Grid Layout with Connectors */}
        <div className="sm:hidden relative">
          {/* Connector Lines */}
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 1 }}
            preserveAspectRatio="none"
          >
            {/* Horizontal line: Step 01 to Step 02 */}
            <line 
              x1="30%" y1="32px" 
              x2="70%" y2="32px" 
              stroke="#14b8a6" 
              strokeWidth="3" 
              strokeLinecap="round"
            />
            
            {/* Diagonal line: Step 02 to Step 03 */}
            <line 
              x1="75%" y1="50px" 
              x2="25%" y2="180px" 
              stroke="#0d9488" 
              strokeWidth="3" 
              strokeLinecap="round"
            />
            
            {/* Horizontal line: Step 03 to Step 04 */}
            <line 
              x1="30%" y1="200px" 
              x2="70%" y2="200px" 
              stroke="#14b8a6" 
              strokeWidth="3" 
              strokeLinecap="round"
            />
          </svg>

          {/* 2x2 Grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-12 relative" style={{ zIndex: 2 }}>
            {howItHelps.map((item) => {
              const IconComponent = iconMap[item.icon];
              
              return (
                <div 
                  key={item.id} 
                  className="text-center relative"
                >
                  {/* Icon Container */}
                  <div className="relative inline-block mb-3">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-400 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-teal-500/30">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    {/* Step Number */}
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-md">
                      {item.step}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xs font-semibold text-gray-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 text-[10px] leading-relaxed px-1">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItHelpsSection;
